import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { formatBusiness } from 'config/time';
import FetchFailed from 'reusable/FetchFailed';
import Pagination from 'reusable/pagination';
import Search from 'reusable/search';
import TableScroll from 'reusable/TableScroll';
import { useLatestRequest } from 'reusable/useLatestRequest';
import { OPERATOR_COMPANY_NEW_PATH, operatorCompanyPath } from 'router/pathName';
import { listCompanies, OPERATOR_PAGE_SIZE, OperatorCompany } from './operatorApi';
import { SuccessNotice, pageToRecover, primaryButton } from './OperatorUi';

const TITLE_ID = 'operator-companies-title';

const address = (company: OperatorCompany) =>
  [company.prefecture, company.city, company.street].filter((part) => part && part.trim()).join(' ') || '-';

/**
 * YAC-2026-009 PR5b。会社の一覧（運営）。検索（会社名）とページ送りは、運転者・機器の一覧と同じ作り
 * （サーバーの search・page・page_size=10、最後に始めた要求の応答だけを使う、失敗は空の一覧と区別する）。
 */
const CompanyList: React.FC = () => {
  const { t } = useTranslation('layout');
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<OperatorCompany[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(
    (location.state as { notice?: string } | null)?.notice ?? null
  );
  const beginRequest = useLatestRequest();

  const load = useCallback(async () => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const result = await listCompanies(page, search);
      if (!isCurrent()) return;
      const recover = pageToRecover(page, OPERATOR_PAGE_SIZE, { count: result.count, rows: result.results.length });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      setRows(result.results);
      setCount(result.count);
      setError(null);
    } catch (failure) {
      if (!isCurrent()) return;
      // ページの外（404）なら、エラーにせず1ページ目を読む。
      const recover = pageToRecover(page, OPERATOR_PAGE_SIZE, { error: failure });
      if (recover !== null) {
        setPage(recover);
        return;
      }
      console.error('Failed to fetch companies', failure);
      setError(failure);
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [beginRequest, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="flex flex-col gap-4 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <h1 id={TITLE_ID} className="text-title font-bold text-neutral/300">
          {t('operator.company.list-title')}
        </h1>
        <Link to={OPERATOR_COMPANY_NEW_PATH} className={primaryButton}>
          {t('operator.company.add')}
        </Link>
      </div>
      <SuccessNotice message={notice} onClose={() => setNotice(null)} />
      <div className="flex flex-col gap-4 rounded-md bg-white p-4">
        <div className="w-80">
          <Search
            placeholder={t('operator.company.search')}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
          />
        </div>
        {loading ? (
          <div role="status" className="py-8 text-center text-neutral/300">
            {t('ui.loading', { ns: 'common' })}
          </div>
        ) : error ? (
          <FetchFailed error={error} onRetry={load} />
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-neutral/300">
            {search ? t('operator.company.no-match') : t('operator.company.empty')}
          </p>
        ) : (
          <TableScroll labelledBy={TITLE_ID}>
            <table className="w-full text-left text-body-2 text-neutral/300">
              <thead className="bg-primary/50">
                <tr>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.company.col-id')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.company.col-name')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.company.col-address')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.company.col-time-zone')}</th>
                  <th scope="col" className="px-3 py-2 font-medium text-right">{t('operator.company.col-managers')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.company.col-created')}</th>
                  <th scope="col" className="px-3 py-2 font-medium">{t('operator.company.col-actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((company) => (
                  <tr key={company.id} className="border-b">
                    <td className="px-3 py-3 text-neutral/100">{company.id}</td>
                    <td className="px-3 py-3 font-medium">{company.name}</td>
                    <td className="px-3 py-3">{address(company)}</td>
                    <td className="px-3 py-3">{company.time_zone || '-'}</td>
                    <td className="px-3 py-3 text-right">{company.manager_count}</td>
                    <td className="px-3 py-3">{formatBusiness(company.create_time, 'yyyy-MM-dd', '-')}</td>
                    <td className="px-3 py-3">
                      <Link
                        to={operatorCompanyPath(company.id)}
                        className="text-primary/300 underline"
                        aria-label={t('operator.company.view-label', { name: company.name })}
                      >
                        {t('operator.company.view')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
        <Pagination allRows={count} rowsPerPage={OPERATOR_PAGE_SIZE} currentPage={page} onPageChange={setPage} />
      </div>
    </section>
  );
};

export default CompanyList;
